import type { ReactNode } from "react";
import { ChevronLeft, Info } from "lucide-react";
import { Link, useMatches, useSearchParams } from "react-router";
import { Tooltip } from "./Tooltip";

/**
 * PageHeader — the shared header for every list page.
 *
 * One row: view tabs on the left, controls and actions on the right. The page
 * title deliberately lives only in the TopBar, so each page renders exactly one
 * `h1` and the header costs a single band instead of five.
 *
 * Tabs are links, not buttons. Every list page already keeps its active view in
 * a search param, so a link gives back/forward, a shareable URL, and
 * `aria-current="page"` for free — none of which a button can express without
 * hand-written ARIA.
 */

export type HeaderTab = {
  key: string;
  label: string;
  /** Rendered as a badge beside the label. `null` while counts are loading. */
  count?: number | null;
  /**
   * Route path. When set the tab navigates by route instead of by search param,
   * which is what a section shell (Website) needs — its views are pages.
   */
  to?: string;
  icon?: ReactNode;
  /** Hover explainer, rendered as an ⓘ beside the label. */
  description?: string;
  /** Rendered as an inert "Soon" chip rather than a link. */
  disabled?: boolean;
};

type PageHeaderProps = {
  /**
   * Visible title. List and section pages omit it — the TopBar names them.
   * Detail pages set it, because the entity's name is the page's subject and a
   * 14px breadcrumb is too quiet to carry it.
   */
  title?: ReactNode;
  /** Back link shown before the title on detail pages. */
  back?: { to: string; label: string };
  /**
   * Whether this header renders the page's `h1`. Default true — it uses
   * `title`, or the route's own name rendered screen-reader-only. Pages that
   * already show a heading of their own (an entity profile card) pass false so
   * the page still has exactly one `h1`.
   */
  heading?: boolean;
  /** Accessible name for the tab strip, e.g. "Client views". */
  tabsLabel?: string;
  tabs?: HeaderTab[];
  activeTab?: string;
  /** Search param the tabs write, e.g. "filter". Required with `tabs`. */
  tabParam?: string;
  /** The tab represented by `tabParam` being absent from the URL. */
  defaultTab?: string;
  /** Search, sort and filter controls. Right-aligned on the same row. */
  controls?: ReactNode;
  /** Actions. Inline from `md` up — use `mobileAction` for smaller screens. */
  actions?: ReactNode;
  /**
   * The primary action, as a floating button below `md`. Every list page needs
   * its create action to survive small screens; `actions` alone is hidden there.
   */
  mobileAction?: { label: string; icon?: ReactNode; onClick: () => void };
  /** Result summary, e.g. "4 of 18 clients". Announced when it changes. */
  status?: ReactNode;
};

export function PageHeader({
  title,
  back,
  heading = true,
  tabsLabel,
  tabs,
  activeTab,
  tabParam,
  defaultTab,
  controls,
  actions,
  mobileAction,
  status,
}: PageHeaderProps) {
  const [searchParams] = useSearchParams();
  const matches = useMatches();

  /** The route's own name, for the heading when the page shows no title. */
  const routeTitle = [...matches]
    .reverse()
    .map((m) => (m.handle as { title?: string } | undefined)?.title)
    .find(Boolean);

  /** URL for a tab, preserving every other param and resetting pagination. */
  const tabHref = (key: string) => {
    const next = new URLSearchParams(searchParams);
    if (tabParam) {
      if (key === defaultTab) next.delete(tabParam);
      else next.set(tabParam, key);
    }
    next.delete("page");
    const qs = next.toString();
    return qs ? `?${qs}` : "";
  };

  return (
    <>
      <div className="px-4 pt-3 pb-3 md:px-7 md:pt-4 md:pb-4">
        <div className="flex items-center gap-x-4 gap-y-2 flex-wrap">
          {/* The page's h1. Visible when the page has a title of its own,
              otherwise carried screen-reader-only by the route's name — the
              breadcrumb already says it on screen. */}
          {heading && !title && <h1 className="sr-only">{routeTitle}</h1>}

          {(back || title) && (
            <div className="flex min-w-0 items-center gap-2.5">
              {back && (
                <Link
                  to={back.to}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-ink-4 transition-colors hover:bg-paper-2 hover:text-ink-2"
                  aria-label={`Back to ${back.label}`}
                >
                  <ChevronLeft size={17} strokeWidth={2} />
                </Link>
              )}
              {title &&
                (heading ? (
                  <h1 className="truncate font-display text-[22px] font-medium leading-tight tracking-tight">
                    {title}
                  </h1>
                ) : (
                  <span className="truncate font-display text-[22px] font-medium leading-tight tracking-tight">
                    {title}
                  </span>
                ))}
            </div>
          )}

          {tabs && tabs.length > 0 && (
            // Always shrinkable, so the strip scrolls instead of colliding with
            // the controls at any width.
            <div className="relative min-w-0 flex-1 2xl:flex-none">
              <nav
                aria-label={tabsLabel}
                className="flex items-stretch gap-5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                {tabs.map((t) => {
                  const isActive = t.key === activeTab;
                  const inner = (
                    <>
                      {t.icon}
                      {t.label}
                      {t.description && (
                        <Tooltip label={t.description} multiline side="bottom">
                          <Info
                            size={12}
                            strokeWidth={1.8}
                            aria-label={`About ${t.label}: ${t.description}`}
                            className="text-ink-5 transition-colors hover:text-ink-3"
                          />
                        </Tooltip>
                      )}
                      {t.count != null && (
                        <span className="rounded-full border hair bg-paper-hi px-1.5 text-[11px] text-ink-3 num">
                          {t.count}
                        </span>
                      )}
                    </>
                  );
                  const shape =
                    "flex min-h-11 shrink-0 items-center gap-2 whitespace-nowrap border-b-2 text-[13.5px] transition-colors";

                  if (t.disabled) {
                    return (
                      <div
                        key={t.key}
                        aria-disabled="true"
                        className={`${shape} cursor-not-allowed border-transparent text-ink-5`}
                      >
                        {inner}
                        <span className="hair rounded-full border px-1.5 text-[9.5px] font-semibold uppercase tracking-wider text-ink-4">
                          Soon
                        </span>
                      </div>
                    );
                  }

                  return (
                    <Link
                      key={t.key}
                      to={t.to ?? tabHref(t.key)}
                      aria-current={isActive ? "page" : undefined}
                      className={`${shape} ${
                        isActive
                          ? "border-tide text-ink font-medium"
                          : "border-transparent text-ink-4 hover:text-ink-2"
                      }`}
                    >
                      {inner}
                    </Link>
                  );
                })}
              </nav>
              {/* Scroll affordance — the strip scrolls rather than wraps on phones. */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-paper md:hidden"
              />
            </div>
          )}

          {/* The controls take their own row until 2xl. A 236px sidebar leaves
              only ~1044px at 1280, which is not enough for tabs + search + sort
              + two buttons on one line. */}
          <div className="flex w-full min-w-0 items-center justify-end gap-2 2xl:w-auto 2xl:flex-1">
            {status && (
              <p role="status" aria-live="polite" className="sr-only">
                {status}
              </p>
            )}
            {controls}
            {actions && (
              <div className="hidden md:flex shrink-0 items-center gap-2 whitespace-nowrap">
                {actions}
              </div>
            )}
          </div>
        </div>
      </div>

      {mobileAction && (
        <button
          type="button"
          onClick={mobileAction.onClick}
          className="fixed bottom-5 right-5 z-30 flex h-12 items-center gap-2 rounded-full bg-tide px-5 text-[14px] font-medium text-paper-hi shadow-lg transition-colors hover:bg-tide-d md:hidden"
        >
          {mobileAction.icon}
          {mobileAction.label}
        </button>
      )}
    </>
  );
}
